import { memo, useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import type { Chat, ChatQueryResponseQuantData } from '@trinity/chat';
import { ChatHintType, ChatActionType  } from '@trinity/chat';
import { trunc } from '@trinity/chat-components/utils';
import { MessageQueryHintContainer, QuantQueryHint } from '../presentation/Message.QueryHint';
import { SubmitActionContext } from './context';
import { QueryFilesSuggestedAction } from './Message.SuggestedActions.Files';

type Props = {
  data: ChatQueryResponseQuantData;
};

export const FindQuantAnalysesResponse = memo((props: Props) => {
  const ctx = useContext(SubmitActionContext);

  useEffect(() => {
    if (!props.data.quants.length) {
      const fullQuery = [
        `Please answer the following question:`,
        ``,
        `"${props.data.query}"`,
        ``,
        `Using only available transcripts and documents.`,
      ].join('\n');

      ctx({
        query: props.data.query,
        type: ChatActionType.QueryFiles,
      }, fullQuery);
    }
  }, [props.data.quants.length, props.data.query]);

  if (!props.data.quants.length) {
    return (
      <div>
        {'No relevant quant analyses found. Searching transcripts and files...'}
      </div>
    );
  }

  return (
    <div>
      <div>
        {'The following analyses were found that may be relevant. Selecting an analysis is no longer supported; try a global search of files instead.'}
      </div>
      <AnalysesText>
        {'Analyses'}
      </AnalysesText>
      {
        props.data.showLowConfidenceWarning &&
        <ConfidenceWarning>
          {'We couldn\'t confidently match your question to the data. Try rephrasing it.'}
        </ConfidenceWarning>
      }
      <MessageQueryHintContainer>
        {props.data.quants.map(quant => {
          const trimmed = trunc(quant.title, 150);
          const hint: Chat.QueryHint.Item = {
            displayValue: trimmed,
            type: ChatHintType.Text,
          };
          const showTooltip = trimmed.length < quant.title.length;
          return (
            <QuantQueryHint
              key={quant.quantItemId}
              hint={hint}
              onClick={noop}
              disabled
              tooltip={showTooltip ? (
                <ExpandedTooltip>
                  <Header>
                    <HeaderLabel>{quant.title}</HeaderLabel>
                  </Header>
                </ExpandedTooltip>
              ) : undefined}
            />
          );
        })}
      </MessageQueryHintContainer>

      <br />

      <QueryFilesSuggestedAction query={props.data.query} />
    </div>
  );
});

// Legacy analyses are shown for history only: the quant chart workflow they used to start is gone.
const noop = () => undefined;

const ExpandedTooltip = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  maxWidth: 200,
  zIndex: 1,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '12px',
  fontFamily: theme.fonts.semiBold,
}));

const ConfidenceWarning = styled.div(({ theme }) => ({
  color: theme.palette.red.main,
  fontSize: '12px',
  marginBottom: '10px',
}));

const AnalysesText = styled.div(({ theme }) => ({
  fontSize: '16x',
  fontFamily: theme.fonts.semiBold,
  marginTop: '10px',
  marginBottom: '10px',
}));